import { isValidCpf } from '@/shared/format/cpf';
import { isValidCnpj } from '@/shared/format/cnpj';
/* eslint-disable lingui/no-unlocalized-strings -- U02: backend enum values; all user-facing labels are translated. */
import type { PersonForBilling, PersonForBillingInput } from '@/modules/person/public';
import type { RuntimeConfig } from '@/shared/config';
import type { Billing } from './billing.wire';
import { civilDateOf, isCivilDate } from '@/shared/format/date';
export type BillingPaymentMethod = 'BankSlip' | 'Pix';
export type BillingParty = NonNullable<Billing['payer']>;
export interface BillingCreateValues {
  walletCode: string;
  walletCodeDisplay?: string | null;
  walletSelected?: unknown;
  personId: string;
  personIdDisplay?: string | null;
  personDiscriminator?: PersonForBillingInput['discriminator'];
  documentNumber: string;
  description: string | null;
  dueDate: string;
  paymentLimitDate: string | null;
  amount: number | null;
  payer: BillingParty | null;
}
export function billingPayer(
  person: PersonForBilling,
  discriminator: PersonForBillingInput['discriminator'],
): BillingParty {
  return {
    name: person.name,
    personTypeValue: discriminator === 'LegalPerson' ? 'Legal' : 'Natural',
    email: person.email ?? null,
    registrationNumber: person.registrationNumber ?? null,
    zipCode: person.address?.zipCode,
    addressName: person.address?.addressName,
    addressNumber: person.address?.number,
    addressComplement: person.address?.complement,
    ...(discriminator === 'LegalPerson' ? { companyName: person.companyName } : {}),
  };
}
export function billingCreateBody(values: BillingCreateValues): Record<string, unknown> {
  return {
    ...values,
    walletCode: values.walletCode,
    amount: Number(values.amount),
    dueDate: new Date(`${values.dueDate}T00:00:00-03:00`).toISOString(),
    paymentLimitDate: values.paymentLimitDate
      ? new Date(`${values.paymentLimitDate}T00:00:00-03:00`).toISOString()
      : null,
  };
}
export function validBillingCreate(values: BillingCreateValues): boolean {
  const payer = values.payer;
  return (
    !!values.walletCode &&
    !!values.personId &&
    !!values.documentNumber &&
    values.documentNumber.length <= 10 &&
    Number.isFinite(values.amount) &&
    (values.amount ?? 0) > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(values.dueDate) &&
    !!payer &&
    typeof payer.email === 'string' &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payer.email) &&
    (payer.zipCode ?? '').replace(/\D/g, '').length === 8 &&
    !!payer.addressName &&
    (typeof payer['addressNumber'] === 'number' ||
      (typeof payer['addressNumber'] === 'string' && payer['addressNumber'].length > 0))
  );
}
export interface BillingParameterValues {
  billingSettingId: string | null;
  communicationSettingId: string | null;
  dueDate: string;
  discount: string | null;
  discountLimitDate: string | null;
  abatment: boolean;
  firstInstructionDebtCollection: string | null;
  secondInstructionDebtCollection: number | null;
  fineAmount: number | string | null | undefined;
  fineTypeValue: string | null | undefined;
  interestAmount: number | string | null | undefined;
  interestTypeValue: string | null | undefined;
  interestCodeValue: string | null | undefined;
  originalDueDate?: string | null | undefined;
  originalDiscountLimitDate?: string | null | undefined;
}
export function billingParameterWireDate(value: string | null, original?: string | null) {
  if (!value) return value;
  if (original && value === original) return original;
  if (original && civilDateOf(original) === value) return original;
  return isCivilDate(value) ? `${value}T00:00:00.000-03:00` : value;
}
export function billingParametersBody(v: BillingParameterValues): Record<string, unknown> {
  return {
    billingSettingId: v.billingSettingId,
    dueDate: billingParameterWireDate(v.dueDate, v.originalDueDate),
    discount: v.discount,
    discountLimitDate: billingParameterWireDate(
      v.discountLimitDate,
      v.originalDiscountLimitDate,
    ),
    communicationSettingId: v.communicationSettingId,
    firstInstructionDebtCollection: v.firstInstructionDebtCollection,
    secondInstructionDebtCollection:
      v.firstInstructionDebtCollection === '06' ? v.secondInstructionDebtCollection : null,
    settings: {
      fineAmount: v.fineTypeValue === 'Free' ? 0 : v.fineAmount,
      fineTypeValue: v.fineTypeValue,
      interestAmount: v.interestTypeValue === 'Free' ? 0 : v.interestAmount,
      interestTypeValue: v.interestTypeValue,
      interestCodeValue: v.interestTypeValue === 'Free' ? null : v.interestCodeValue,
    },
  };
}
export function validBillingParameters(v: BillingParameterValues): boolean {
  return (
    (!v.abatment || (v.discount !== null && v.discount !== '' && !!v.discountLimitDate)) &&
    (v.firstInstructionDebtCollection !== '06' ||
      (v.secondInstructionDebtCollection !== null &&
        v.secondInstructionDebtCollection >= 3 &&
        v.secondInstructionDebtCollection <= 30))
  );
}
export function billingPixEnabled(config: RuntimeConfig, walletCode: number): boolean {
  if (Number(config['BOLEPIX']) !== 1) return false;
  const codes = config['BOLEPIX_WALLET_CODES'];
  return !Array.isArray(codes) || codes.length === 0 || codes.map(Number).includes(walletCode);
}
export function billingMethods(
  methods: readonly BillingPaymentMethod[],
  pixEnabled: boolean,
): BillingPaymentMethod[] {
  const allowed = (['BankSlip', 'Pix'] as const).filter(
    (method) => methods.includes(method) && (method !== 'Pix' || pixEnabled),
  );
  return allowed.length ? allowed : ['BankSlip'];
}
export function billingParameters(row: Billing): BillingParameterValues {
  return {
    billingSettingId: row.billingSettingId ?? null,
    communicationSettingId: row.communicationSettingId ?? null,
    dueDate: row.dueDate ?? '',
    discount: row.discount == null ? null : String(row.discount),
    discountLimitDate: row.discountLimitDate ?? null,
    abatment: row.discount != null,
    firstInstructionDebtCollection: null,
    secondInstructionDebtCollection: null,
    fineAmount: undefined,
    fineTypeValue: undefined,
    interestAmount: undefined,
    interestTypeValue: undefined,
    interestCodeValue: undefined,
    originalDueDate: row.dueDate ?? null,
    originalDiscountLimitDate: row.discountLimitDate ?? null,
  };
}

// U02: GuarantorFormSchema.ts:5 and formFieldValidator.ts:49-66,182-197.
export function validBillingGuarantor(document: string, config: RuntimeConfig): boolean {
  const flags = config['FEATURE_FLAGS'];
  const alphanumeric = !!(
    flags &&
    typeof flags === 'object' &&
    'CNPJ_ALPHANUMERIC' in flags &&
    flags['CNPJ_ALPHANUMERIC']
  );
  const clean = document.replace(alphanumeric ? /[^A-Za-z0-9]/g : /\D/g, '');
  if (clean.length === 14 && alphanumeric && /[A-Za-z]/.test(clean)) return true;
  return clean.length === 11 ? isValidCpf(clean) : clean.length === 14 && isValidCnpj(clean);
}
